import React, { useEffect } from "react";
import { ReactComponent as LogoDark } from "app/assets/images/logo-dark.svg";
import { ReactComponent as LogoWhite } from "app/assets/images/logo-white.svg";
import { ReactComponent as Chevron } from "app/assets/icons/caret-right.svg";
import { ReactComponent as Logout } from "app/assets/icons/logout.svg";
import Text from "app/styles/Text";
import Card from "app/components/ui/Card";
import logout from "app/lib/logout";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ACTIVE_PROFILE_ID_QUERY_PARAM,
  HOME_PATH,
} from "app/constants/variables";
import { ActiveProfile } from "app/models/profile";
import LoaderContainer from "app/components/ui/LoaderContainer";
import { useAppTheme } from "app/providers/AppThemeProvider";

function ProfileSelector({ profiles }: { profiles: ActiveProfile[] }) {
  const navigate = useNavigate();
  const theme = useAppTheme();
  const [searchParams] = useSearchParams();
  const activeProfileId = searchParams.get(ACTIVE_PROFILE_ID_QUERY_PARAM);
  const previousPathname = searchParams.get("from");

  useEffect(() => {
    if (!!profiles && (profiles?.length === 1 || activeProfileId)) {
      goToProfile(activeProfileId ?? undefined);
    }
  }, [profiles, activeProfileId]);

  const goToProfile = (id?: string) => {
    if (id) {
      navigate(`${previousPathname ?? HOME_PATH}?active_profile_id=${id}`);
    } else {
      navigate(previousPathname ?? HOME_PATH);
    }
  };

  return profiles?.length === 1 ? (
    <LoaderContainer loading screen />
  ) : (
    <div>
      <div tw="flex items-center justify-between border-b border-[#00000014] dark:border-[#FFFFFF14] h-[64px] px-[24px]">
        {theme === "dark" ? (
          <LogoWhite tw="h-[20px]" />
        ) : (
          <LogoDark tw="h-[20px]" />
        )}
      </div>

      <div tw="w-full md:w-[600px] mt-[60px] mx-auto">
        <Text semiBold tw="text-[28px] mb-0">
          Select an account to continue
        </Text>
        <Text subdued>
          Use your personal or business account and switch between them anytime
          within the app.
        </Text>

        <Card tw="p-6 w-full mt-[20px]">
          {profiles?.map((profile) => (
            <button
              onClick={() => goToProfile(profile.id)}
              key={profile.id}
              tw="not-last-of-type:pb-[16px] not-last-of-type:mb-[16px] not-last-of-type:border-b border-[#00000014] dark:border-[#FFFFFF14] flex items-center justify-between w-full text-left"
            >
              <div tw="flex items-center">
                <div tw="flex">
                  <div tw="bg-[#F2F2F2] border-[#E5E5E5] text-[#B2B2B2] flex items-center justify-center w-[40px] h-[40px] rounded-full">
                    <Text semiBold tw="text-[16px] text-[#0D0D0D]">
                      {profile.name.slice(0, 1).toUpperCase()}
                    </Text>
                  </div>
                  <div tw="ml-4">
                    <Text semiBold tw="text-[16px]">
                      {profile.name}
                    </Text>
                    <Text subdued>
                      {profile.profile_type === "business"
                        ? "Business"
                        : "Personal"}{" "}
                      account
                    </Text>
                  </div>
                </div>
              </div>
              <div tw="text-[#B2B2B2]">
                <Chevron />
              </div>
            </button>
          ))}
        </Card>

        <div tw="mt-[30px] flex items-center justify-between">
          <button
            tw="flex items-center text-error50"
            onClick={() => logout("")}
          >
            <Logout />
            <Text tw="ml-2">Logout</Text>
          </button>

          <Text subdued>© Busha {new Date().getFullYear()}</Text>
        </div>
      </div>
    </div>
  );
}

export default ProfileSelector;
